import { WarningOutlined } from '@ant-design/icons';
import { Modal as AModal, Button } from 'antd';
import { ContainerState } from 'containers/CheckoutPage/types';
import useResize from 'hooks/useResize';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import translations from 'translations';
import media from 'utils/mediaStyle';
import utilsMessages from 'utils/messages';
import { VOUCHER_STATUS } from '../../../containers/CheckoutPage/constants';
import useMessages from '../../../containers/CheckoutPage/hooks/useMessages';
import SVGIcon from '../../SVGIcon/SVGIcon';
import ChevronRightIcon from './chevronRightIcon';
import VoucherPopup from './VoucherPopup';
import VoucherTag from './voucherTag.svg';

const ConfirmInvalidVoucherModal = styled(AModal)`
  .ant-modal-content {
    padding: 24px 16px 12px;
  }
  .ant-modal-title {
    font-size: 24px;
    font-weight: 500;
    color: #595959;
    margin-bottom: 20px !important;
  }
  .ant-modal-footer {
    display: flex;
    justify-content: center;
    button {
      flex-grow: 1;
    }
    margin-top: 20px !important;
  }
`;

const ConfirmModalTitle = styled.div`
  font-size: 18px;
  margin: -8px -16px 0;
  padding: 0 16px;
  text-align: center;
`;

const RemoveVoucherButton = styled(Button)`
  border-radius: 4px !important;
  border-color: #7cca84;
  color: #4b9c63;
`;

const ConfirmContentModal = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  text-align: center;
  color: #585a60;
`;

const VoucherSectionContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;

  background: #fff;
  box-shadow: 0 4px 24px 0 rgba(58, 85, 9, 0.04);

  ${media.lg`
    padding: 20px 24px;
    border-radius: 8px;
  `}
`;

const LeftComponent = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  ${media.lg`
    gap: 6px;
  `}
`;

const RightComponent = styled.div<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};
`;

const KamereoVoucherText = styled.span`
  font-size: 14px;
  line-height: 18px;
  color: #111827;
  font-weight: 400;

  ${media.lg`
    font-size: 16px;
    font-weight: 500;
    line-height: 18px;
  `}
`;

const SelectVoucherButton = styled(Button)`
  &.ant-btn {
    color: #6b7280;
    font-size: 14px;
    font-weight: 500;
    line-height: 18px;
    background: none !important;
    padding: 0 !important;
    height: auto !important;
  }

  ${({ isAppliedVoucher }) =>
    isAppliedVoucher &&
    `
    &.ant-btn {
      color: #E56910 !important;
      background: none !important;
    }
  `}
`;

const voucherStatusMessages = (voucherState: ContainerState['vouchers']) => {
  switch (voucherState?.status) {
    case VOUCHER_STATUS.INVALID:
    case VOUCHER_STATUS.EXPIRED:
    case VOUCHER_STATUS.NOT_SATISFIED:
      return <FormattedMessage {...utilsMessages.voucherInvalid} />;
    case VOUCHER_STATUS.APPLIED:
      return voucherState?.currentAppliedVoucher?.code;
    default:
      return <FormattedMessage {...utilsMessages.voucherButton} />;
  }
};

interface VoucherSectionProps {
  subTotal: number;
  voucherState: ContainerState['vouchers'];
  onGetVouchers: (data: any) => void;
  isOpenConfirmInvalidVoucherPopup?: boolean;
  setIsOpenConfirmInvalidVoucherPopup: (open: boolean) => void;
  isVoucherNotValid: boolean;
  onApplyVoucher: (data: any) => void;
}

const VoucherSection = (props: VoucherSectionProps) => {
  const {
    subTotal,
    onGetVouchers,
    onApplyVoucher,
    voucherState,
    isOpenConfirmInvalidVoucherPopup,
    setIsOpenConfirmInvalidVoucherPopup,
    isVoucherNotValid,
  } = props;

  const { isDesktop } = useResize();

  const loggedIn = !!localStorage.getItem('token');

  const [isOpenVoucher, setIsOpenVoucher] = useState(false);
  const { contextHolder, handleShowSuccess, handleShowError } = useMessages();
  const prevAppliedVoucherStatus = useRef(voucherState?.status);

  // Voucher
  const onClickVoucher = () => {
    setIsOpenVoucher(true);
    onGetVouchers({ total: subTotal });
  };

  useEffect(() => {
    if (voucherState?.currentAppliedVoucher) {
      onApplyVoucher({
        code: voucherState?.currentAppliedVoucher.code,
        total: subTotal,
      });
    }
  }, [subTotal]);

  useEffect(() => {
    if (voucherState?.status && voucherState?.status !== prevAppliedVoucherStatus.current) {
      if (voucherState?.status === VOUCHER_STATUS.APPLIED) {
        handleShowSuccess();
      } else {
        handleShowError();
      }
    }
    prevAppliedVoucherStatus.current = voucherState?.status;
  }, [voucherState?.status]);

  return isDesktop ? (
    <Fragment>
      {contextHolder}
      <VoucherSectionContainer>
        <LeftComponent>
          <SVGIcon src={VoucherTag} renderAsImage={false} />
          <KamereoVoucherText>
            <FormattedMessage {...utilsMessages.voucherLabel} />
          </KamereoVoucherText>
        </LeftComponent>
        <RightComponent disabled={!loggedIn} onClick={loggedIn ? onClickVoucher : undefined}>
          <SelectVoucherButton
            iconPosition="start"
            icon={isVoucherNotValid ? <WarningOutlined /> : null}
            color="default"
            isAppliedVoucher={!!voucherState?.status}
            type="text"
            disabled={!loggedIn}
          >
            {voucherStatusMessages(voucherState)}
          </SelectVoucherButton>
          <ChevronRightIcon color={!!voucherState?.status ? '#E56910' : '#6B7280'} />
        </RightComponent>
      </VoucherSectionContainer>

      <VoucherPopup
        total={subTotal}
        open={isOpenVoucher}
        setOpen={setIsOpenVoucher}
        voucherState={voucherState}
        onApplyVoucher={onApplyVoucher}
      />

      <ConfirmInvalidVoucherModal
        open={isOpenConfirmInvalidVoucherPopup}
        title={
          <ConfirmModalTitle>
            <FormattedMessage {...utilsMessages.voucherConfirmTitle} />
          </ConfirmModalTitle>
        }
        centered
        onOk={() => {
          setIsOpenConfirmInvalidVoucherPopup(false);
          onClickVoucher();
        }}
        onCancel={() => setIsOpenConfirmInvalidVoucherPopup(false)}
        closable={false}
        okText={translations(utilsMessages.viewVoucher)}
        okButtonProps={{
          size: 'large',
          style: {
            borderRadius: '4px',
          },
        }}
        cancelButtonProps={{
          size: 'large',
          style: {
            borderRadius: '4px',
          },
        }}
        footer={(_, { OkBtn }) => (
          <>
            <RemoveVoucherButton
              size="large"
              onClick={() => {
                onApplyVoucher({});
                setIsOpenConfirmInvalidVoucherPopup(false);
              }}
            >
              <FormattedMessage {...utilsMessages.removeVoucher} />
            </RemoveVoucherButton>
            <OkBtn />
          </>
        )}
      >
        <ConfirmContentModal>
          <FormattedMessage {...utilsMessages.voucherConfirmContent} />
        </ConfirmContentModal>
      </ConfirmInvalidVoucherModal>
    </Fragment>
  ) : null;
};

export default VoucherSection;
